import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  FaultDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type FaultDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderDateControl,
  renderTextareaControl,
  renderCheckboxControl,
  renderButtonControl,
  type ControlConfig,
} from '../../formRenderFunctions'

export { FaultDeviceFormSchema, type FaultDeviceFormData } from './schema'

export type FaultDeviceComposerProps = {
  deviceNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
}

const validateField = (name: keyof FaultDeviceFormData, value: unknown): string | null => {
  const shape = FaultDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function FaultDeviceComposer(props: FaultDeviceComposerProps) {
  const { deviceNumber, onSuccess, onError, onCancel, showHeader = true } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber })
  const deviceData = deviceDataRaw?.content

  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => onError?.(error),
  })

  const form = useAppForm<FaultDeviceFormData>({
    schema: FaultDeviceFormSchema,
    defaultValues: {
      deviceFaultyDate: new Date().toISOString().split('T')[0],
      deviceFaultyNotes: '',
      deviceReturned: false,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as FaultDeviceFormData
      if (isDeviceLoading) return

      const payload = transformToApiRequest(formValue, deviceNumber) as any
      const existingDevice = deviceData?.device || {}
      const existingFaultInfo = deviceData?.faultInfo || {}

      const mergedPayload = {
        ...payload,
        device: {
          ...existingDevice,
          ...payload.device,
          deviceNumber,
        },
        faultInfo: {
          ...existingFaultInfo,
          ...payload.faultInfo,
          deviceNumber,
          deviceFaultInfoNumber: (existingFaultInfo as any)?.deviceFaultInfoNumber,
        },
      }

      await upsertDevice.mutateAsync(mergedPayload)
      onSuccess?.()
    },
  })

  const renderControl = (control: ControlConfig) => {
    if (control.htmlControl === 'date') {
      return (
        <form.AppField
          key={control.name}
          name={control.name as 'deviceFaultyDate'}
          validators={{
            onBlur: ({ value }) =>
              validateField(control.name as keyof FaultDeviceFormData, value) || undefined,
          }}
        >
          {(bound) =>
            renderDateControl(
              {
                ...control,
                required: true,
              },
              bound,
            )
          }
        </form.AppField>
      )
    }

    if (control.htmlControl === 'textarea') {
      return (
        <form.AppField
          key={control.name}
          name={control.name as 'deviceFaultyNotes'}
          validators={{
            onBlur: ({ value }) =>
              validateField(control.name as keyof FaultDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => renderTextareaControl(control, bound)}
        </form.AppField>
      )
    }

    if (control.htmlControl === 'checkbox') {
      return (
        <form.AppField
          key={control.name}
          name={control.name as 'deviceReturned'}
          validators={{
            onBlur: ({ value }) =>
              validateField(control.name as keyof FaultDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => renderCheckboxControl(control, bound)}
        </form.AppField>
      )
    }

    return null
  }

  const renderFormContent = () =>
    groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map((control) => (
            <div key={control.name}>{renderControl(control)}</div>
          ))}
        </div>
      )
    })

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            {FORM_CONFIG.formDescription && (
              <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
            )}
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-end gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}
          {renderButtonControl(
            {
              label: upsertDevice.isPending
                ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Submitting...')
                : (FORM_CONFIG.submitButton?.label ?? 'Mark as Faulty'),
              variant: 'default',
              type: 'submit',
            },
            { SubmitButton: form.SubmitButton },
          )}
        </div>
      </div>
    </form.AppForm>
  )
}
